import * as React from 'react';
import { Page } from '../../cookbook-ui/Page_SC';
import { XIAnimation, XISVG, SVGs, SearchBox, SnackBar, _LottiesOptions, SnackBarProps } from '@inmoment/the-kitchen';
import { Link } from 'react-router-dom';
import { VisualTile } from '../../cookbook-ui/visual-components/VisualTile/VisualTile';
import { Blank } from '../intro-pages/Home/FAQ_SC';
import { GraphicSVG } from '../../cookbook-ui/visual-components/automated-components/graphics/GraphicSVG';
import { _SVGMeta } from '../../cookbook-ui/visual-components/automated-components/graphics/metaExports';

export interface Props {}
export const Graphics: React.FC<Props> = () => {
  const [query, setQuery] = React.useState('');
  const [snackBarObject, setSnackBarObject] = React.useState<SnackBarProps | null>(null);
  const textArea = React.useRef<HTMLTextAreaElement>(null);

  const matchesQuery = React.useCallback((value: string) => value.toLowerCase().includes(query.toLowerCase()), [query]);

  const filterAndSearchMeta = React.useCallback(
    (filter: (meta: (typeof _SVGMeta)[number]) => boolean) => {
      const metaMatchesQuery = (meta: (typeof _SVGMeta)[number]) => matchesQuery(meta.name) || meta.tags.some(matchesQuery);
      return _SVGMeta.filter((meta) => filter(meta) && metaMatchesQuery(meta));
    },
    [matchesQuery],
  );

  const lottieArray = React.useMemo(() => _LottiesOptions.filter(matchesQuery), [matchesQuery]);
  const illustrationArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'illustration'), [filterAndSearchMeta]);
  const skewArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'skew'), [filterAndSearchMeta]);
  const visualArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'demo'), [filterAndSearchMeta]);
  const sentimentArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'sentiment'), [filterAndSearchMeta]);
  const backgroundArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'background'), [filterAndSearchMeta]);
  const inmomentArray = React.useMemo(() => filterAndSearchMeta((m) => m.name.includes('inmoment')), [filterAndSearchMeta]);
  const companyArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === 'company'), [filterAndSearchMeta]);
  const miscArray = React.useMemo(() => filterAndSearchMeta((m) => m.category === ''), [filterAndSearchMeta]);

  React.useEffect(() => {
    const scroll = () => {
      const lastBit = window.location.href.split('/').slice(-1)[0];
      if (lastBit.split('#').length > 1) {
        const element = document.querySelector(`#${lastBit.split('#')[1]}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('load', scroll);
    return () => window.removeEventListener('load', scroll);
  }, []);

  function handleClick(text: string) {
    if (textArea.current) textArea.current.value = text;
    textArea.current?.select();
    document.execCommand('copy');
    setSnackBarObject({
      mode: 'default',
      text: `${text} copied to clipboard`,
      dismissTime: 5000,
      autoDismiss: true,
      id: new Date().getTime().toString(),
    });
  }

  return (
    <Page>
      <p id="Graphics" className="kxi-text-xlarge kxi-text-600">
        Graphics
      </p>
      <br />
      <div style={{ display: 'flex' }}>
        <p style={{ flex: 1 }}>This page contains svg illustrations and animations for use in XI.</p>
        <div style={{ width: 260, marginLeft: 24 }}>
          <SearchBox placeholder="search by name or tag" onChange={(e) => setQuery(e.target.value)} onCancelClick={() => setQuery('')} />
        </div>
      </div>
      <hr />
      <br />
      {lottieArray.length > 0 && (
        <>
          <p className="kxi-text-xlarge kxi-text-600">
            Use the <Link to="/Visuals">XIAnimation</Link> component to access these svgs.
          </p>
          <br />
          <p id="Animations" className="kxi-text-large kxi-text-600">
            Animations
          </p>
          <br />
          <br />
          <br />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
              gridAutoRows: '1fr',
            }}
          >
            {lottieArray.map((name) => (
              <VisualTile key={name} text={name} onClick={() => handleClick(name)} downloadPath={`/assets/kitchen/v2/lotties-jsons/${name}.json`}>
                <XIAnimation animation={name} width={90} height={90} loop />
              </VisualTile>
            ))}
          </div>
          <br />
          <br />
          <hr />
          <br />
          <br />
        </>
      )}

      {(illustrationArray.length > 0 ||
        skewArray.length > 0 ||
        visualArray.length > 0 ||
        backgroundArray.length > 0 ||
        companyArray.length > 0 ||
        miscArray.length > 0) && (
        <>
          <p id="SVGs" className="kxi-text-xlarge kxi-text-600">
            Use the <Link to="/Visuals">XISVG</Link> component to access these svgs.
          </p>
          <br />
          <p id="Illustrations" className="kxi-text-large kxi-text-600">
            Illustrations
          </p>
          {illustrationArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p className="kxi-text-xsmall kxi-text-300">Illustration SVGs</p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {illustrationArray.map((SVG) => (
                  <VisualTile
                    key={SVG.name}
                    text={SVG.name}
                    onClick={() => handleClick(SVG.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${SVG.name}.svg`}
                  >
                    <GraphicSVG svg={SVG.name} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {inmomentArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="InMoment-Logos" className="kxi-text-600 kxi-text-medium">
                InMoment Logos
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {inmomentArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                    color={icon.name.includes('white') || icon.name.includes('kitchen') ? 'orca' : 'white'}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {skewArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="Skewmorphics" className="kxi-text-600 kxi-text-medium">
                Skewmorphics
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {skewArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {visualArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="Visual-Demos" className="kxi-text-600 kxi-text-medium">
                Visual Demos
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {visualArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {sentimentArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="Sentiments" className="kxi-text-600 kxi-text-medium">
                Sentiments
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {sentimentArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {backgroundArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="Backgrounds" className="kxi-text-600 kxi-text-medium">
                Backgrounds
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {backgroundArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {companyArray.length > 0 && (
            <>
              <br />
              <br />
              <hr />
              <p id="Company-Logos" className="kxi-text-600 kxi-text-medium">
                Company Logos
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {companyArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
          {miscArray.length > 0 && (
            <>
              <br />
              <br />
              <br />
              <p id="Misc-SVGs" className="kxi-text-600 kxi-text-medium">
                Miscellaneous SVGs
              </p>
              <br />
              <br />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(144px, 1fr))',
                  gridAutoRows: '1fr',
                }}
              >
                {miscArray.map((icon) => (
                  <VisualTile
                    key={icon.name}
                    text={icon.name}
                    onClick={() => handleClick(icon.name)}
                    downloadPath={`/assets/kitchen/v2/svgs/${icon.name}.svg`}
                  >
                    <XISVG svg={icon.name as SVGs} style={{ width: 60, height: 60 }} />
                  </VisualTile>
                ))}
              </div>
            </>
          )}
        </>
      )}
      <Blank />
      {lottieArray.length === 0 &&
        illustrationArray.length === 0 &&
        skewArray.length === 0 &&
        visualArray.length === 0 &&
        sentimentArray.length === 0 &&
        backgroundArray.length === 0 &&
        companyArray.length === 0 &&
        miscArray.length === 0 && <p>No results found.</p>}
      <textarea style={{ position: 'fixed', top: 0, opacity: 0 }} ref={textArea} readOnly />
      <SnackBar {...snackBarObject} />
    </Page>
  );
};
